// vg-binding: P7.D-63
//
// apps/vendor/src/features/orders/BarcodeScannerInput.tsx
//
// Phase 7 Wave 14 Task 55 — barcode scanner input.
//
// Keyboard-wedge scanners deliver scans as fast keyboard input ending in
// Enter. We capture the input, parse `ORD-` prefix and emit the resolved
// item/order ID to caller. Manual fallback always available (caller's own
// transition buttons remain reachable).

import { useState, type FormEvent } from 'react';

import { Input } from '@printway/ui';

import { useTranslation } from '../../lib/i18n/index.js';

interface BarcodeScannerInputProps {
  onScan: (value: string) => void;
  placeholder?: string;
}

export function BarcodeScannerInput({
  onScan,
  placeholder,
}: BarcodeScannerInputProps): JSX.Element {
  const { t } = useTranslation('orders');
  const [value, setValue] = useState('');
  const [lastScan, setLastScan] = useState<string | null>(null);

  function submit(e: FormEvent) {
    e.preventDefault();
    const trimmed = value.trim();
    if (!trimmed) return;
    // Strip ORD- prefix if present; pass remainder.
    const cleaned = trimmed.toUpperCase().startsWith('ORD-')
      ? trimmed.slice(4)
      : trimmed;
    onScan(cleaned);
    setLastScan(trimmed);
    setValue('');
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-2">
      <Input
        data-testid="orders-barcode-scanner-input"
        autoFocus
        type="text"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder={placeholder ?? t('barcodeScanner.placeholder')}
        className="font-mono"
      />
      {lastScan ? (
        <p className="text-xs text-pw-text-muted">
          {t('barcodeScanner.justScanned')} <code className="font-mono text-pw-text">{lastScan}</code>
        </p>
      ) : null}
    </form>
  );
}

export default BarcodeScannerInput;
